import { t } from 'i18next';
import { useMutation } from 'react-query';

import { NotificationPosition } from 'core/enums';
import { useHandleErrors } from 'core/hooks';
import { accessService } from 'pricing/services';

interface IAccessActions {
  selectionCount?: number;
  position?: NotificationPosition;
}

export const useAccessActions = ({ selectionCount, position }: IAccessActions = {}) => {
  return {
    ...useUpsertAccess(),
    ...useRemoveAccess(selectionCount, position),
  };
};

const useUpsertAccess = () => {
  const { errors } = useHandleErrors({
    success: ({ pricingAccessItem }) =>
      pricingAccessItem?.id
        ? { message: t('messageAccessUpdateSuccess') }
        : { message: t('messageAccessCreateSuccess') },
    loading: ({ pricingAccessItem }) =>
      pricingAccessItem?.id
        ? { message: t('messageAccessUpdateInProgress') }
        : { message: t('messageAccessCreateInProgress') },
    error: ({ pricingAccessItem }) =>
      pricingAccessItem?.id
        ? { title: t('errorTitleAccessUpdate') }
        : { title: t('errorTitleAccessCreate') },
    position: NotificationPosition.FORM,
  });

  const { mutateAsync } = useMutation((data) => accessService.upsertPricingProfileAccess(data), {
    ...errors,
  });

  return { upsertAccess: mutateAsync };
};

const useRemoveAccess = (
  selectionCount: number,
  position: NotificationPosition = NotificationPosition.TOP
) => {
  const { errors } = useHandleErrors({
    success: {
      message: t('messageAccessRemoveSuccess', { count: selectionCount }),
    },
    loading: {
      message: t('messageAccessRemoveInProgress', { count: selectionCount }),
    },
    error: {
      title: t('errorTitleAccessRemove', { count: selectionCount }),
    },
    position,
  });

  const { mutateAsync } = useMutation((data) => accessService.removePricingProfileAccess(data), {
    ...errors,
  });

  return { removeAccess: mutateAsync };
};
